/**
 * DuplicateVmsWidget — Dashboard widget that shows detected duplicate VMs
 * and allows manual merge.
 *
 * A "duplicate group" = multiple VirtVM records sharing the same name
 * (case-insensitive), e.g., SRVL6DK001 (HyperV) + srvl6dk001 (OS agent).
 *
 * The recommended primary is always the one with the active/most recent agent.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ExclamationTriangleIcon, ArrowsRightLeftIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const STATUS_COLORS = {
  online:    { bg: 'var(--green-50)', color: 'var(--green-600)', label: 'Agente online' },
  offline:   { bg: 'var(--red-50)',   color: 'var(--red-600)',   label: 'Agente offline' },
  unknown:   { bg: 'var(--bg-toolbar)', color: 'var(--text-muted)', label: 'Sin agente' },
  'no-agent':{ bg: 'var(--bg-toolbar)', color: 'var(--text-muted)', label: 'Sin agente' },
};
const _DEFAULT_STATUS = { bg: 'var(--bg-toolbar)', color: 'var(--text-muted)', label: 'Sin agente' };

export default function DuplicateVmsWidget({ companyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [merging, setMerging] = useState(null);        // { group, sourceId, targetId }
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState(null);
  const [mergeError, setMergeError] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getDashboardDuplicateVms(companyId);
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const startMerge = async (group, sourceId, targetId) => {
    setMerging({ group, sourceId, targetId });
    setPreview(null);
    setMergeError(null);
    setPreviewLoading(true);
    try {
      const p = await api.previewMerge(companyId, 'virt_vm', sourceId, 'virt_vm', targetId);
      setPreview(p.data || p);
    } catch (e) {
      setMergeError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmMerge = async () => {
    if (!merging) return;
    setPreviewLoading(true);
    setMergeError(null);
    try {
      await api.executeMerge(companyId, 'virt_vm', merging.sourceId, 'virt_vm', merging.targetId);
      setMergeSuccess(merging.group.normalized_name);
      setMerging(null);
      setPreview(null);
      setTimeout(() => { setMergeSuccess(null); load(); }, 2000);
    } catch (e) {
      setMergeError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const cancelMerge = () => { setMerging(null); setPreview(null); setMergeError(null); };

  if (loading) return null;
  if (error) return null;
  if (!data || data.total_groups === 0) return null;

  const { total_groups, total_duplicates, groups } = data;

  return (
    <>
      {/* ── Banner ── */}
      <div style={{
        background: 'var(--amber-50)', border: '1px solid var(--amber-600)',
        borderRadius: 8, padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        marginBottom: 0,
      }} onClick={() => setExpanded(e => !e)}>
        <ExclamationTriangleIcon style={{ width: 18, height: 18, color: 'var(--amber-600)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, color: 'var(--amber-600)', fontSize: '0.88rem' }}>
            {total_groups} grupo{total_groups !== 1 ? 's' : ''} de VMs duplicadas detectado{total_groups !== 1 ? 's' : ''}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginLeft: 6 }}>
            ({total_duplicates} registro{total_duplicates !== 1 ? 's' : ''} extra)
          </span>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
          {expanded ? '▲ Ocultar' : '▼ Revisar'}
        </span>
      </div>

      {mergeSuccess && (
        <div style={{ background: 'var(--green-50)', border: '1px solid var(--green-600)', borderRadius: 6, padding: '8px 14px', fontSize: '0.82rem', color: 'var(--green-600)', fontWeight: 600, marginTop: 6 }}>
          ✅ Fusión completada: {mergeSuccess}
        </div>
      )}

      {/* ── Expanded list ── */}
      {expanded && !merging && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
          {groups.map((group, gi) => (
            <div key={group.normalized_name} style={{
              borderBottom: gi < groups.length - 1 ? '1px solid var(--border)' : 'none',
              padding: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                  📦 {group.normalized_name}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {group.count} registros encontrados
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.candidates.map((vm, vi) => {
                  const agentStyle = STATUS_COLORS[vm.agent_status] || _DEFAULT_STATUS;
                  const isPrimary = vm.id === group.recommended_primary_id;
                  return (
                    <div key={vm.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', borderRadius: 6,
                      background: isPrimary ? 'var(--green-50)' : 'var(--bg-toolbar)',
                      border: isPrimary ? '1px solid var(--green-600)' : '1px solid var(--border)',
                      fontSize: '0.82rem',
                    }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{vm.name}</span>
                        {isPrimary && (
                          <span style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, color: 'var(--green-600)', background: 'var(--green-50)', padding: '1px 5px', borderRadius: 3 }}>
                            ✓ Principal recomendado
                          </span>
                        )}
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {vm.os_name || '—'} · Creado: {vm.created_at ? new Date(vm.created_at).toLocaleDateString('es-ES') : '—'}
                          {vm.hypervisor_name && ` · Hipervisor: ${vm.hypervisor_name}`}
                        </div>
                      </div>
                      <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 600, background: agentStyle.bg, color: agentStyle.color }}>
                        {agentStyle.label}
                        {vm.last_seen_at && ` · ${new Date(vm.last_seen_at).toLocaleDateString('es-ES')}`}
                      </span>
                      {!isPrimary && (
                        <button
                          type="button"
                          onClick={() => startMerge(group, vm.id, group.recommended_primary_id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 3,
                            background: 'var(--accent)', color: '#fff',
                            border: 'none', borderRadius: 5,
                            padding: '3px 9px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600,
                          }}
                          title="Fusionar este en el principal"
                        >
                          <ArrowsRightLeftIcon style={{ width: 12, height: 12 }} />
                          Fusionar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Merge confirmation modal ── */}
      {merging && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={cancelMerge}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, padding: 24, width: 500, maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <ArrowsRightLeftIcon style={{ width: 20, height: 20, color: 'var(--accent)' }} />
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>Confirmar fusión de VMs</span>
            </div>

            {previewLoading && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Generando vista previa…</p>}

            {mergeError && <p style={{ color: 'var(--red-600, #dc2626)', fontSize: '0.82rem' }}>⚠ {mergeError}</p>}

            {preview && !previewLoading && (
              <div style={{ background: 'var(--bg-toolbar)', borderRadius: 6, padding: 12, marginBottom: 14, fontSize: '0.82rem' }}>
                <p style={{ marginBottom: 6, color: 'var(--text-secondary)' }}>
                  El registro <strong>{preview.source?.name || `#${merging.sourceId}`}</strong> (duplicado) será fusionado en
                  <strong> {preview.target?.name || `#${merging.targetId}`}</strong> (principal con agente).
                </p>
                <ul style={{ paddingLeft: 16, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  <li>Los datos del principal se mantienen.</li>
                  <li>El registro duplicado se elimina.</li>
                  <li>Esta acción <strong>no se puede deshacer</strong>.</li>
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={cancelMerge}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-toolbar)', cursor: 'pointer', fontSize: '0.82rem' }}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cancelar
              </button>
              <button type="button" onClick={confirmMerge}
                disabled={previewLoading || !preview}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 5, border: 'none', background: previewLoading || !preview ? 'var(--text-muted)' : 'var(--accent)', color: '#fff', cursor: previewLoading || !preview ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                <CheckIcon style={{ width: 14, height: 14 }} /> Confirmar fusión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
